<template>
  <v-col align="center" class="loader">
    <v-row justify="center" style="width: 100%" class="ma-0">
      <!-- Button to open file input -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            rounded
            outlined
            color="primary"
            class="mx-2"
            :disabled="disabled"
            @click="pickFile"
          >
            <v-icon>video_call</v-icon>
          </v-btn>
        </template>
        <span>{{ $translate('UPLOAD_VIDEO') }}</span>
      </v-tooltip>

      <!-- Button to remove current video -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            rounded
            outlined
            color="red accent-3"
            class="mx-2 mt-2"
            :class="hasVideo ? '' : 'd-none'"
            :disabled="disabled"
            @click="deleteFile"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </template>
        <span>{{ $translate('DELETE_VIDEO') }}</span>
      </v-tooltip>
    </v-row>

    <!-- Video preview -->
    <div :class="hasVideo ? '' : 'd-none'" class="mt-4">
      <video :src="videoUrl" controls class="loader_video">
        <!-- Shown only if browser doesn't support video element -->
        {{ $translate('VIDEO_NOT_SUPPORT') }}
      </video>
    </div>

    <div :class="fileUploading ? '' : 'd-none'" class="mt-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- Hidden input element -->
    <input
      type="file"
      style="display: none"
      :ref="loaderId"
      accept="video/*"
      @change="onFilePicked"
    />
  </v-col>
</template>

<script>
import doRequest, { uploadFile } from '@/api/dssRequest';
import utility from '@/shared/utility';

export default {
  name: 'VideoLoader',
  components: {},
  props: {
    /**
     * {
     *  src: [VIDEO_URL],
     * }
     */
    config: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  computed: {
    hasVideo() {
      if (this.videoUrl != null && this.videoUrl !== '') {
        return true;
      }

      return false;
    },
  },
  watch: {
    config() {
      this.init();
    },
  },
  data() {
    return {
      loaderId: utility.getRandomString(8, true, true, true, false),
      maxSizeInBytes: 104857600, // 100 MB
      file: null,
      fileId: '',
      videoUrl: '',
      fileUploading: false,

      fileManagerObj: 'LocationsNode_FM',
    };
  },
  created() {},
  beforeDestroy() {},
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (
        this.config != null &&
        this.config.src != null &&
        this.config.src.trim() !== ''
      ) {
        this.videoUrl = this.config.src;
      }
    },
    pickFile() {
      if (this.$refs[this.loaderId] == null) {
        return;
      }

      this.$refs[this.loaderId].click();
    },
    onFilePicked() {
      // Safety check
      if (
        this.$refs[this.loaderId] == null ||
        this.$refs[this.loaderId].files == null ||
        this.$refs[this.loaderId].files.length === 0
      ) {
        return;
      }

      // Clear current video file
      this.removeFile();

      // Get file from input element
      [this.file] = this.$refs[this.loaderId].files;
      // Clear the input element, so next time the change will be called.
      this.$refs[this.loaderId].value = null;

      // Validate file before uploading starts
      if (!this.isValidFile()) {
        return;
      }

      // Send file info to server, and expect [FileId] to be returned
      this.addFile()
        .then((response) => {
          // File added, use returned [FileId] to upload file to server
          this.fileId = response.data.FileId;

          // Upload file data to server
          this.uploadFileToServer();
        })
        .catch(() => {
          // Failed to add file
          this.fileUploading = false;

          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('UPLOAD_FILE_FAILED'),
            color: 'error',
          });
        });
    },
    isValidFile() {
      if (this.file == null) {
        return false;
      }

      if (this.file.size > this.maxSizeInBytes) {
        const tempErrorStr = this.$translate('FILE_SIZE_EXCEED_MAX');
        const errorStr = utility.sprintf(
          tempErrorStr,
          this.maxSizeInBytes.toString()
        );

        window.EventBus.fire('activateSnackBar', {
          text: errorStr,
          color: 'warning',
        });

        return false;
      }

      return true;
    },
    addFile() {
      const partnerId = this.$store.getters['partner/partnerDetails']
        ? this.$store.getters['partner/partnerDetails'].PartnerId
        : '';

      const payload = {
        Name: this.file.name,
        Type: this.file.type,
        FileType: 'Video',
        OwnerId: partnerId,
        OwnerType: 'Partner',
        Ranking: 1,
        Filename: this.file.name,
      };

      this.fileUploading = true;

      return new Promise((resolve, reject) => {
        doRequest(resolve, reject, 'addFile', this.fileManagerObj, payload);
      });
    },
    uploadFileToServer() {
      const formData = new FormData();
      formData.append('image', this.file);
      formData.append('filename', this.file.name);
      formData.append('FileId', this.fileId);

      uploadFile(formData)
        .then(() => {
          // Video uploaded successfully, preview video
          this.fileUploading = false;
          this.videoUrl = URL.createObjectURL(this.file);
          this.$emit('change', { FileId: this.fileId });
        })
        .catch(() => {
          // Failed to upload the video
          this.fileUploading = false;
          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('UPLOAD_FILE_FAILED'),
            color: 'error',
          });
        });
    },
    removeFile() {
      this.videoUrl = '';
      this.fileId = '';
      this.file = null;
      this.fileUploading = false;
    },
    deleteFile() {
      // Currently no backend communication needed.
      this.removeFile();
      this.$refs[this.loaderId].value = null;
      this.$emit('change', { FileId: '' });
    },
  },
};
</script>

<style scoped lang="scss">
.loader {
  border: 1px solid grey;
  border-radius: 5px;
  background-color: #ffffff;
  transition-property: border-color;
  transition-duration: 0.2s;
}
.loader:hover {
  border-color: black;
}
.loader_video {
  width: 100%;
  height: auto;
  max-height: 80vh;
}
</style>
